/**
 * iter-72 — Scheduler / metronome domain module extracted from /lib/audioEngine.js
 *
 * Manages the metronome — a tempo-synced click track that fires lock-step
 * with the engine's AudioContext clock (not setInterval, which drifts under
 * load). The metronome runs INDEPENDENTLY of transport state by design so
 * the user can practice along to a steady beat without rolling the timeline.
 *
 * Exports a `Metronome` class so the engine owns a single instance and the
 * state (on/off, next-tick time, scheduling timer) stays bundled.
 */

/** Schedule a single metronome click at `time` (AudioContext seconds).
 *  Short triangle blip routed direct to ctx.destination so it bypasses
 *  the mastering / limiter chain — keeps the click reliably audible even
 *  when the master is heavily compressed. */
export function scheduleClick(ctx, time) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.frequency.value = 1000;
  osc.type = "triangle";
  gain.gain.setValueAtTime(0, time);
  gain.gain.linearRampToValueAtTime(0.15, time + 0.001);
  gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + 0.06);
}

export class Metronome {
  /** @param {AudioContext} ctx
   *  @param {() => number} getTempoBpm — read live so a tempo change mid-run
   *    picks up at the next scheduled click without restart. */
  constructor(ctx, getTempoBpm) {
    this.ctx = ctx;
    this.getTempoBpm = getTempoBpm;
    this.on = false;
    this._nextTime = 0;
    this._timer = null;
  }

  /** Start scheduling clicks. Uses the standard Web Audio scheduler pattern
   *  (rAF-ish setTimeout looking ~200 ms into the future) so the JS event
   *  loop hiccupping never causes a missed beat. */
  start() {
    if (this.on) return;
    this.on = true;
    this._nextTime = this.ctx.currentTime + 0.05;
    const scheduleAhead = () => {
      if (!this.on) return;
      const horizon = this.ctx.currentTime + 0.2;
      const secondsPerBeat = 60 / this.getTempoBpm();
      while (this._nextTime < horizon) {
        scheduleClick(this.ctx, this._nextTime);
        this._nextTime += secondsPerBeat;
      }
      this._timer = setTimeout(scheduleAhead, 25);
    };
    scheduleAhead();
  }

  stop() {
    this.on = false;
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  toggle() {
    if (this.on) this.stop();
    else this.start();
    return this.on;
  }

  /** Tempo changed externally — rebase the next-click time so the new
   *  cadence kicks in immediately (without dropping the current bar). */
  rebase() {
    if (this.on) {
      this._nextTime = this.ctx.currentTime + 0.02;
    }
  }
}
